import { RefCallback, useCallback, useEffect, useLayoutEffect, useState } from "react";

export interface IUseScrolledIntoViewConfiguration {
	rootMargin?: IntersectionObserverInit["rootMargin"];
	threshold?: IntersectionObserverInit["threshold"];
	onScrolledIntoView?: () => void;
}

export const useIntersection = <TWrapperElement extends Element = HTMLElement, TObservedElement extends Element = HTMLElement>({
	rootMargin = "0px",
	threshold = 1,
	onScrolledIntoView,
}: Readonly<IUseScrolledIntoViewConfiguration>) => {
	const [wrapperContainerRef, setWrapperContainerRef] = useState<TWrapperElement | null>(null);
	const [observedElementRef, setObservedElementRef] = useState<TObservedElement | null>(null);

	const [isIntersecting, setIsIntersecting] = useState(false);
	const [shouldFire, setShouldFire] = useState(false);

	const wrapperContainerCallbackRef: RefCallback<TWrapperElement> = useCallback((ref) => {
		setIsIntersecting(true);
		setShouldFire(false);
		setWrapperContainerRef(ref);
	}, []);

	const observedElementCallbackRef: RefCallback<TObservedElement> = useCallback((ref) => {
		setIsIntersecting(true);
		setShouldFire(false);
		setObservedElementRef(ref);
	}, []);

	useLayoutEffect(() => {
		if (!observedElementRef || !wrapperContainerRef) {
			return () => {};
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				setIsIntersecting(entry.isIntersecting);
				if (entry.isIntersecting) {
					setShouldFire(true);
				}
			},
			{
				root: wrapperContainerRef,
				rootMargin,
				threshold,
			}
		);
		observer.observe(observedElementRef);

		return () => {
			observer.disconnect();
		};
	}, [rootMargin, threshold, observedElementRef, wrapperContainerRef]);

	useEffect(() => {
		if (shouldFire && isIntersecting) {
			setShouldFire(false);
			onScrolledIntoView?.();
		}
	}, [shouldFire, isIntersecting, onScrolledIntoView]);

	return {
		wrapperContainerRef: wrapperContainerCallbackRef,
		observedElementRef: observedElementCallbackRef,
		isIntersecting,
	};
};
